import { Box, FormControlLabel, Checkbox } from '@material-ui/core';
import { apiActions } from 'app/core/api/api-redux';
import {
  useCreateRepairLocation,
  useCurrentCompany,
  useDeleteRepairLocation,
  useRepairLocations,
  useSetRepairersActivity,
  useGetRepairPersonelAddress,
} from 'app/core/api/company/api-company';
import { PageDetails } from 'app/core/api/company/api-company.types';
import { RepairLocation } from 'app/core/api/repairer/api-repairer.types';
import { appEnv } from 'app/core/environment';
import { getErrorMsg } from 'app/core/errors/error-utils';
import {
  MissingItemError,
  NotFoundError,
} from 'app/core/errors/error-variants';
import Button from 'app/shared/components/button/Button';
import FetchifyAddressForm, {
  DetailedAddress,
  FfyCommand,
  getAddressString,
} from 'app/shared/components/fetchify-address/FetchifyAddressForm';
import FormTextInput from 'app/shared/components/form-input/FormTextInput';
import useInputForm from 'app/shared/components/form-input/utilities/use-input-form';
import IconLib from 'app/shared/components/icon/icon-lib';
import CollapseSection from 'app/shared/components/list/CollapseSection';
import NameValueList from 'app/shared/components/list/NameValueList';
import {
  ErrorText,
  PadExpand,
  PaleOverlay,
} from 'app/shared/components/misc';
import Body from 'app/shared/components/typography/body/Body';
import { useShowInfo } from 'app/shared/context-hooks/use-show-info.hook';
import { useSetPageTitle } from 'app/shared/context-hooks/use-titlebar.hook';
import Form from 'app/shared/frames/Form';
import ClassName from 'app/styles/ClassName';
import styledFuncs from 'app/styles/styledFuncs';
import {
  useEffectOnce,
  useEffectOnChanges,
  useObservableSend,
} from 'app/tools/hooks/misc-hooks';
import { BLANK$ } from 'app/tools/utils';
import { useObservableEagerState } from 'observable-hooks';
import { useEffect, useMemo, useState } from 'react';
import Geocode from 'react-geocode';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { BehaviorSubject, from } from 'rxjs';
import { finalize, map, switchMap, tap } from 'rxjs/operators';
import styled from 'styled-components/macro';
import { useMovePage } from '../create-business-profile/business-profile-utils';
import BottomPageAndSubmitActions from '../create-business-profile/input-pages/components/BottomActions';
import BottomEditActions from '../create-business-profile/input-pages/components/BottomEditActions';

const StyledCollapser = styled(CollapseSection)`
  margin-top: ${styledFuncs.pad(-1)};
  font-weight: normal !important;
`;

type FormValues = Pick<RepairLocation, 'locationDisplayName'>;

type Props = {
  firstTime?: boolean;
  isIndividual?: boolean;
};
export default function UpdateRepairLocations({
  firstTime,
  isIndividual,
}: Props): JSX.Element {
  useSetPageTitle('Repair Locations');
  const [showPersonalAddress, setShowPersonalAddress] =
    useState(false);
  const [addressCheckBox, setAddressCheckBox] = useState(false);
  const { request$: repairActivity } = useSetRepairersActivity();
  useEffectOnce(() => {
    const pageDetails: PageDetails = {
      pagename: 'Repair Locations',
    };
    repairActivity(pageDetails).subscribe();
  });
  const { showSnack, showDialog } = useShowInfo();
  const { nextPage } = useMovePage();

  const history = useHistory();
  const dispatch = useDispatch();

  useEffect(() => {
    Geocode.setApiKey(appEnv.GOOGLE_API_KEY);
  }, []);

  const {
    request$: createRepairLocation$,
    error: createLocationError,
  } = useCreateRepairLocation({ fresh: true });

  const {
    resource: companyAddress,
    request: loadPersonAddress$,
    // loading: loadingMissingAddress,
  } = useGetRepairPersonelAddress();

  // const defaultValues = loading ? undefined : company;

  const { resource: company, loadingMissing: loadingMissingCompany } =
    useCurrentCompany();

  const { request$: deleteRepairLocation$ } =
    useDeleteRepairLocation();

  const {
    resource: repairLocations,
    request: refreshRepairLocations,
    loadingMissing: loadingMissingLocations,
  } = useRepairLocations({ fresh: true });

  const [submittingLocation, setSubmittingLocation] = useState(false);

  const addExpanded$ = useMemo(
    () => new BehaviorSubject(!!firstTime),
    [firstTime]
  );
  const [sendCommand, command$] = useObservableSend<FfyCommand>();

  const { inputProps, getValues, reset } = useInputForm<FormValues>({
    apiError: createLocationError,
  });

  const submitRepairLocation = (data: DetailedAddress) =>
    BLANK$.pipe(
      tap(() => setSubmittingLocation(true)),
      switchMap(() =>
        from(Geocode.fromAddress(getAddressString(data)))
      ),
      map(res => res?.results?.[0].geometry.location),
      tap(location => {
        if (!location) {
          showSnack({
            message:
              'Failed to find geolocation coordinates for address',
            alertType: 'error',
          });
          throw new MissingItemError('geocode location result');
        }
      }),
      switchMap(({ lat, lng }) =>
        createRepairLocation$({
          locationDisplayName: getValues().locationDisplayName,
          ...data,
          latitude: lat,
          longitude: lng,
        })
      ),
      tap(() => {
        showSnack({ message: 'Location added!' });
        addExpanded$.next(false);
        sendCommand('reset');
        reset();
        refreshRepairLocations();
      }),
      finalize(() => setSubmittingLocation(false))
    );

  const deleteRepairLocation = (id: number) => {
    const nameOfDeleteTarget = repairLocations?.find(
      r => r.id === id
    )?.locationDisplayName;
    if (!nameOfDeleteTarget) {
      throw new NotFoundError(`location with id ${id}`);
    }

    const handleConfirmDelete = () => {
      // NOTE: show immediate changes by editing redux, will then get
      // confirmed data on next update.
      dispatch(
        apiActions.repairLocations.set(
          repairLocations?.filter(l => l.id !== id) || []
        )
      );
      deleteRepairLocation$(id).subscribe({
        next: () => refreshRepairLocations(),
        error: (err: unknown) =>
          showSnack({
            message: getErrorMsg(err),
            alertType: 'error',
          }),
      });
    };

    showDialog({
      title: 'Confirm delete',
      message: `Are you sure you want to delete repair location ${nameOfDeleteTarget}`,
      actions: [
        { text: 'Cancel' },
        { text: 'Confirm Delete', callback: handleConfirmDelete },
      ],
    });
  };

  const editable$ = useMemo(
    () => new BehaviorSubject(!!firstTime),
    [firstTime]
  );
  const editable = useObservableEagerState(editable$);

  useEffectOnChanges(() => {
    if (!editable) {
      addExpanded$.next(false);
      history.goBack();
    }
  }, [addExpanded$, editable, history]);

  // const getPersonalAddress = () => {
  //   loadPersonAddress$();
  //   setShowPersonalAddress(true);
  // };

  const handleChangeUsePersonalAddress = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    // setState({ ...state, [event.target.name]: event.target.checked });
    setAddressCheckBox(event.target.checked);
    if (event.target.checked) {
      loadPersonAddress$();
      setShowPersonalAddress(true);
    } else {
      setShowPersonalAddress(false);
    }
  };

  return (
    <>
      {repairLocations?.length ? (
        <Body color="primary" style={{ fontWeight: 'normal' }}>
          Current repair locations
        </Body>
      ) : null}
      <Box position="relative" style={{ fontWeight: 500 }}>
        {!editable && <PaleOverlay style={{ height: '150%' }} />}
        {repairLocations?.length ? (
          <div>
            <NameValueList
              listItems={repairLocations.map(l => ({
                id: l.id,
                name: l.locationDisplayName,
                value: l.postcode || l.addrLine1,
              }))}
              noTopDivider
              onDelete={deleteRepairLocation}
            />
          </div>
        ) : null}
        <StyledCollapser
          title={
            repairLocations?.length
              ? 'Add another repair location'
              : 'Add repair location(s)'
          }
          titleColor="primary"
          className={ClassName.NO_AUTO_MARGIN}
          icons={{ expand: IconLib.ADD_NEW }}
          expanded$={addExpanded$}
        >
          <Form>
            <FormTextInput
              controlName="locationDisplayName"
              placeholder="Repair Location Name"
              {...inputProps}
            />
          </Form>
          {/* {!repairLocations?.length && (
            <Button
              pending={loadingMissingAddress}
              onClick={() => getPersonalAddress()}
            >
              Use Business Address
            </Button>
          )} */}

          <FetchifyAddressForm
            defaultValues={
              showPersonalAddress ? companyAddress : undefined
            }
            editable
            extraWidth
            promptUnsaved
            lockCountryCode={company?.country}
            onSubmit={submitRepairLocation}
            error={createLocationError}
            {...{ command$ }}
          />
          <Body tiny>Asterisks (*) indicate required fields</Body>
          {!repairLocations?.length && (
            <FormControlLabel
              control={
                <Checkbox
                  checked={addressCheckBox}
                  onChange={handleChangeUsePersonalAddress}
                  name="checkedB"
                  color="primary"
                />
              }
              label="Use address as repair location"
            />
          )}

          <ErrorText message={createLocationError} />

          <Button
            pending={submittingLocation}
            onClick={() => sendCommand('submit')}
          >
            Add Repair Location
          </Button>
        </StyledCollapser>
      </Box>
      <PadExpand />
      {firstTime ? (
        <BottomPageAndSubmitActions
          isSubmit
          // onSubmit={() => nextPage(isIndividual)}
          onSubmit={() => nextPage(true)}
          isIndividual={isIndividual}
          locationCount={!repairLocations?.length}
        />
      ) : (
        <BottomEditActions
          backInsteadOfCancel
          loading={loadingMissingLocations || loadingMissingCompany}
          {...{ editable$ }}
        />
      )}
    </>
  );
}
